import { Slider } from '@/components/ui/slider';
import { useState, useCallback, useRef } from 'react';
import type { StreamChapter } from '@/lib/shared-types';
import type { StreamStatus } from '@/lib/shared-types/constants';

interface StreamProgressBarProps {
  currentTime: number;
  duration: number;
  streamStatus: StreamStatus;
  onSeek: (value: number[]) => void;
  formatTime: (time: number) => string;
  isLiveStream?: boolean;
  liveBufferDuration?: number;
  seekableStart?: number;
  chapters?: StreamChapter[];
  isRecording?: boolean;
}

export function StreamProgressBar({
  currentTime,
  duration,
  streamStatus,
  onSeek,
  formatTime,
  isLiveStream = false,
  liveBufferDuration = 0,
  seekableStart = 0,
  chapters = [],
  isRecording = false
}: StreamProgressBarProps) {
  // Contract: duration for recordings, liveBufferDuration for live
  // Slider operates on: 0 → seekWindow
  // Video operates on: seekStart → seekEnd
  const seekStart = isLiveStream ? seekableStart : 0;
  const seekEnd = isLiveStream
    ? seekableStart + (liveBufferDuration || 0)
    : duration;

  // seekWindow must be positive
  // Parent ensures valid values (finite, > 0) before passing to this component
  const seekWindow = Math.max(0.1, seekEnd - seekStart);
  
  // For recordings: currentTime is absolute (0 to duration)
  // For live: currentTime is relative (0 to liveBufferDuration)
  // Normalize to slider coordinates (0 to seekWindow)
  let sliderValue: number;
  if (isLiveStream) {
    // Live: currentTime is already relative, just clamp it
    sliderValue = Math.max(0, Math.min(currentTime, seekWindow));
  } else {
    // Recording: currentTime is absolute, convert to relative for slider
    // Since seekStart = 0 for recordings, this is just currentTime clamped
    const relativeTime = currentTime - seekStart;
    sliderValue = Math.max(0, Math.min(relativeTime, seekWindow));
  }
  
  // Final safety clamp to ensure slider value never exceeds max
  const clampedSliderValue = Math.max(0, Math.min(sliderValue, seekWindow));

  // Convert slider relative value back to absolute time for seeking
  const handleSeek = (value: number[]) => {
    const relative = value[0];
    // Pass absolute time to parent
    onSeek([seekStart + relative]);
  };

  // Preview thumbnail state
  const [showPreview, setShowPreview] = useState(false);
  const [previewPosition, setPreviewPosition] = useState(0);
  const [previewTime, setPreviewTime] = useState(0);
  const [previewThumbnail, setPreviewThumbnail] = useState<string | null>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Generate Cloudflare Stream preview thumbnail URL
  const getPreviewThumbnailUrl = useCallback((time: number): string | null => {
    // For Cloudflare Stream, we can generate preview thumbnails using the sprite sheet pattern
    // Pattern: https://customer-xxx.cloudflarestream.com/{videoUid}/thumbnail?time={time}s
    // This would need the video UID which we don't have here
    // For now, return null and let the parent provide chapter thumbnails if available
    return null;
  }, []);

  // Handle mouse move on progress bar for preview
  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!sliderRef.current || isLiveStream) return; // No preview for live streams

    const rect = sliderRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = x / rect.width;
    const hoverTime = percentage * seekWindow + seekStart;

    setPreviewPosition(x);
    setPreviewTime(hoverTime);
    setShowPreview(true);

    // Try to get thumbnail for this time (would need video UID)
    const thumbnailUrl = getPreviewThumbnailUrl(hoverTime);
    setPreviewThumbnail(thumbnailUrl);
  }, [seekWindow, seekStart, isLiveStream, getPreviewThumbnailUrl]);

  const handleMouseLeave = useCallback(() => {
    setShowPreview(false);
  }, []);

  // Handle chapter marker click
  const handleChapterClick = useCallback((chapter: StreamChapter) => {
    // Seek to chapter start time
    onSeek([chapter.startTime]);
  }, [onSeek]);

  // Calculate position for chapter markers
  const getChapterPosition = (chapter: StreamChapter): number => {
    const chapterStart = chapter.startTime;
    if (isLiveStream) {
      // For live streams, chapters are relative to seekStart
      return ((chapterStart - seekStart) / seekWindow) * 100;
    } else {
      // For recordings, chapters are absolute times
      return (chapterStart / duration) * 100;
    }
  };

  return (
    <div className="mb-4 relative">
      {/* Progress bar container with mouse tracking */}
      <div
        ref={sliderRef}
        className="relative w-full"
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <Slider
          value={[clampedSliderValue]}
          max={seekWindow}
          step={0.1}
          onValueChange={handleSeek}
          className="w-full cursor-pointer"
        />
        
        {/* Chapter markers overlay */}
        {chapters.length > 0 && !isLiveStream && (
          <div className="absolute top-0 left-0 right-0 h-full pointer-events-none">
            {chapters.map((chapter) => {
              const position = getChapterPosition(chapter);
              // Only show chapters within the seekable range
              if (position < 0 || position > 100) return null;
              
              return (
                <div
                  key={chapter.id}
                  className="absolute top-0 h-full w-0.5 bg-yellow-400/60 pointer-events-auto cursor-pointer group"
                  style={{ left: `${position}%` }}
                  onClick={() => handleChapterClick(chapter)}
                  title={chapter.title}
                >
                  {/* Chapter tooltip on hover */}
                  <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-black/90 text-white text-xs rounded px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {chapter.title}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Preview tooltip */}
      {showPreview && !isLiveStream && (
        <div
          className="absolute bottom-full mb-2 transform -translate-x-1/2 bg-black/90 rounded-lg overflow-hidden shadow-lg"
          style={{ left: `${previewPosition}px` }}
        >
          {previewThumbnail ? (
            <img
              src={previewThumbnail}
              alt="Preview"
              className="w-32 h-20 object-cover"
            />
          ) : (
            <div className="w-32 h-20 bg-gray-800 flex items-center justify-center">
              <span className="text-white text-xs">{formatTime(previewTime)}</span>
            </div>
          )}
          <div className="bg-black/80 text-white text-xs px-2 py-1 text-center">
            {formatTime(previewTime)}
          </div>
        </div>
      )}

      {/* Time display */}
      <div className="flex justify-between text-xs text-white/80 mt-1">
        <span>{formatTime(clampedSliderValue)}</span>
        <span>{isLiveStream ? 'LIVE' : formatTime(seekWindow)}</span>
      </div>
    </div>
  );
}